import { useState, useRef, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Upload, Download, Share2, Type, Image as ImageIcon, Sparkles, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const MEME_TEMPLATES = [
  { id: 1, name: 'Drake Hotline Bling', url: 'https://i.imgflip.com/30b1gx.jpg', topText: 'Bad thing', bottomText: 'Good thing' },
  { id: 2, name: 'Distracted Boyfriend', url: 'https://i.imgflip.com/1ur9b0.jpg', topText: 'Me', bottomText: 'New trend' },
  { id: 3, name: 'Two Buttons', url: 'https://i.imgflip.com/1g8my4.jpg', topText: 'Button 1', bottomText: 'Button 2' },
  { id: 4, name: 'Change My Mind', url: 'https://i.imgflip.com/24y43o.jpg', topText: 'Your opinion', bottomText: '' },
  { id: 5, name: 'Woman Yelling at Cat', url: 'https://i.imgflip.com/345v97.jpg', topText: 'When someone', bottomText: 'Cat vibes' },
  { id: 6, name: 'Bernie Sanders', url: 'https://i.imgflip.com/4x6d0h.jpg', topText: 'I am once again', bottomText: 'Asking for' },
];

export default function MemeMakerEditor() {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [topText, setTopText] = useState('');
  const [bottomText, setBottomText] = useState('');
  const [textColor, setTextColor] = useState('#FFFFFF');
  const [fontSize, setFontSize] = useState(48);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (selectedTemplate || uploadedImage) {
      drawMeme();
    }
  }, [selectedTemplate, uploadedImage, topText, bottomText, textColor, fontSize]);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setUploadedImage(null);
    setTopText(template.topText);
    setBottomText(template.bottomText);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target.result);
        setSelectedTemplate(null);
        setTopText('');
        setBottomText('');
      };
      reader.readAsDataURL(file);
    }
  };

  const drawMeme = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      // Set canvas size
      canvas.width = 800;
      canvas.height = (800 * img.height) / img.width;

      // Draw image
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Configure text
      ctx.fillStyle = textColor;
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 3;
      ctx.font = `bold ${fontSize}px Impact, Arial Black, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';

      // Draw top text
      if (topText) {
        const words = topText.toUpperCase().split(' ');
        let line = '';
        let y = 20;
        const lineHeight = fontSize + 10;

        words.forEach((word, i) => {
          const testLine = line + word + ' ';
          const metrics = ctx.measureText(testLine);
          if (metrics.width > canvas.width - 40 && i > 0) {
            ctx.strokeText(line, canvas.width / 2, y);
            ctx.fillText(line, canvas.width / 2, y);
            line = word + ' ';
            y += lineHeight;
          } else {
            line = testLine;
          }
        });
        ctx.strokeText(line, canvas.width / 2, y);
        ctx.fillText(line, canvas.width / 2, y);
      }

      // Draw bottom text
      if (bottomText) {
        ctx.textBaseline = 'bottom';
        const words = bottomText.toUpperCase().split(' ');
        let line = '';
        let lines = [];
        const lineHeight = fontSize + 10;

        words.forEach((word, i) => {
          const testLine = line + word + ' ';
          const metrics = ctx.measureText(testLine);
          if (metrics.width > canvas.width - 40 && i > 0) {
            lines.push(line);
            line = word + ' ';
          } else {
            line = testLine;
          }
        });
        lines.push(line);

        let y = canvas.height - 20;
        for (let i = lines.length - 1; i >= 0; i--) {
          ctx.strokeText(lines[i], canvas.width / 2, y);
          ctx.fillText(lines[i], canvas.width / 2, y);
          y -= lineHeight;
        }
      }
    };

    img.src = uploadedImage || selectedTemplate?.url || '';
  };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = 'meme.png';
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Meme downloaded!');
    });
  };

  const handleShare = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    try {
      canvas.toBlob(async (blob) => {
        const file = new File([blob], 'meme.png', { type: 'image/png' });
        
        if (navigator.share && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: 'Check out my meme!',
            text: 'Made with MemeMaker Pro'
          });
          toast.success('Shared successfully!');
        } else {
          toast.info('Share not supported. Use download instead!');
        }
      });
    } catch (error) {
      toast.error('Failed to share');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/meme-landing" className="flex items-center gap-2 text-white hover:text-yellow-400 transition-colors">
              <ArrowLeft className="h-5 w-5" />
              <span className="font-semibold">Back</span>
            </Link>
            <div className="flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-yellow-400" />
              <h1 className="text-xl font-bold text-white">MimeMaker</h1>
            </div>
            <div className="w-20" />
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Templates & Upload */}
          <div className="space-y-6">
            {/* Upload Section */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Upload className="h-5 w-5 text-yellow-400" />
                Upload Your Image
              </h2>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="w-full bg-yellow-400 text-purple-900 hover:bg-yellow-300 font-bold"
                size="lg"
              >
                <ImageIcon className="mr-2 h-5 w-5" />
                Choose Image
              </Button>
            </div>

            {/* Templates */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-4">Popular Templates</h2>
              <div className="grid grid-cols-2 gap-3 max-h-[500px] overflow-y-auto">
                {MEME_TEMPLATES.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    className={`cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                      selectedTemplate?.id === template.id
                        ? 'border-yellow-400 ring-2 ring-yellow-400/50'
                        : 'border-white/20 hover:border-yellow-400/50'
                    }`}
                  >
                    <img
                      src={template.url}
                      alt={template.name}
                      className="w-full h-32 object-cover"
                    />
                    <div className="bg-black/40 p-2">
                      <p className="text-white text-xs font-medium text-center">{template.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Editor & Preview */}
          <div className="space-y-6">
            {/* Canvas Preview */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-4">Preview</h2>
              <div className="bg-white/5 rounded-lg p-4 flex items-center justify-center">
                {!selectedTemplate && !uploadedImage ? (
                  <div className="text-center py-12">
                    <ImageIcon className="h-16 w-16 text-white/20 mx-auto mb-4" />
                    <p className="text-white/60">Select a template or upload an image to start</p>
                  </div>
                ) : (
                  <canvas
                    ref={canvasRef}
                    className="max-w-full h-auto rounded-lg shadow-2xl"
                  />
                )}
              </div>
            </div>

            {/* Text Editor */}
            {(selectedTemplate || uploadedImage) && (
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 space-y-4">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Type className="h-5 w-5 text-yellow-400" />
                  Add Text
                </h2>
                
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Top Text</label>
                  <input
                    type="text"
                    value={topText}
                    onChange={(e) => setTopText(e.target.value)}
                    placeholder="Enter top text..."
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-yellow-400"
                  />
                </div>

                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Bottom Text</label>
                  <input
                    type="text"
                    value={bottomText}
                    onChange={(e) => setBottomText(e.target.value)}
                    placeholder="Enter bottom text..."
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-yellow-400"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">Text Color</label>
                    <input
                      type="color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="w-full h-12 rounded-lg cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">Font Size: {fontSize}px</label>
                    <input
                      type="range"
                      min="24"
                      max="72"
                      value={fontSize}
                      onChange={(e) => setFontSize(Number(e.target.value))}
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleDownload}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold"
                    size="lg"
                  >
                    <Download className="mr-2 h-5 w-5" />
                    Download
                  </Button>
                  <Button
                    onClick={handleShare}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-bold"
                    size="lg"
                  >
                    <Share2 className="mr-2 h-5 w-5" />
                    Share
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}